<script setup lang="ts">
import { computed } from 'vue';

import { constant } from '@/constant';
import { getTranslation } from '@/utils/data-display-utils';
import { Module, ProjectModuleImageTextConfigDto } from '@/store/module.interface';
import { getAltText } from '@/utils/image.helper';
import { Locales } from '@/store/language.interface';

const props = defineProps<{
  module: Module;
  languageEnabled?: Locales[];
}>();

const moduleConfig = props.module.config as ProjectModuleImageTextConfigDto;
const hasImage = computed(() => moduleConfig.image && moduleConfig.image.url !== '');
</script>

<template>
  <div class="text-module-wrapper">
    <div :class="{ highlight: moduleConfig.highlight }">
      <div class="text-module">
        <div class="text-module__content">
          <div
            class="text-module__content-image col"
            v-if="hasImage && moduleConfig.imageAlignment === constant.ProjectModule.Alignment.Left"
          >
            <img
              :src="moduleConfig.image.url"
              :alt="getAltText(moduleConfig.image, 'alt-text.project.module-image', languageEnabled)"
            />
          </div>
          <div
            class="text-module__content-text col"
            v-if="moduleConfig.imageAlignment !== constant.ProjectModule.Alignment.Center"
          >
            <h2>{{ getTranslation(props.module, 'title', languageEnabled) }}</h2>
            <div v-html="getTranslation(moduleConfig, 'content', languageEnabled)" />
          </div>
          <div
            class="text-module__content-image col"
            v-if="hasImage && moduleConfig.imageAlignment === constant.ProjectModule.Alignment.Right"
          >
            <img
              :src="moduleConfig.image.url"
              :alt="getAltText(moduleConfig.image, 'alt-text.project.module-image', languageEnabled)"
            />
          </div>
          <div
            class="text-module__align-center"
            v-if="moduleConfig.imageAlignment === constant.ProjectModule.Alignment.Center"
          >
            <h2>{{ getTranslation(props.module, 'title', languageEnabled) }}</h2>
            <img
              v-if="hasImage"
              :src="moduleConfig.image.url"
              :alt="getAltText(moduleConfig.image, 'alt-text.project.module-image', languageEnabled)"
            />
            <div v-html="getTranslation(moduleConfig, 'content', languageEnabled)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.text-module-wrapper {
  width: 100%;

  .highlight {
    background-color: $color-neutral-grey-blue-150;
    border-radius: 8px;
    padding: $space-lg;
  }

  .text-module {
    display: flex;
    flex-direction: column;
    gap: $space-md;

    h2 {
      margin-bottom: $space-sm !important;
    }

    &__content {
      display: flex;
      flex-direction: row;
      gap: $space-lg;

      &-text {
        font-size: $body-text;
      }

      &-image {
        img {
          width: 100%;
          max-width: 360;
          border-radius: 8px;
        }
      }
    }

    &__align-center {
      display: flex;
      flex-direction: column;
      gap: $space-md;
      width: 100%;

      h2 {
        margin: 0 !important;
      }

      img {
        width: inherit;
        border-radius: 8px;
      }
    }
  }
}

@media screen and (max-width: $breakpoint-md) {
  .text-module-wrapper {
    .text-module {
      &__content {
        flex-direction: column;
      }
    }
  }
}
</style>
